import React, { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import IslamFuturisticShell from '../components/islam/IslamFuturisticShell';
import IslamContentList from '../components/islam/IslamContentList';
import MosquesDirectoryList from '../components/mosques/MosquesDirectoryList';

const VALID_SLUGS = ['mosques', 'quran', 'hadith', 'calendar', 'zakat', 'education'];

/** Placeholder / unfinished Islam tools — keep reachable but out of search indexes. */
const NOINDEX_SECTION_SLUGS = new Set(['quran', 'hadith', 'calendar', 'zakat']);

const SECTION_CONFIG = {
  mosques: {
    browserTitle: 'Mosques — Islam | PakPoints',
    metaDescription:
      'Discover mosques listed on PakPoints—locations, timings, and society communities in one directory.',
    icon: '🕌',
    heading: 'Mosques',
    tagline:
      'Browse verified mosque listings shared by societies—directions, prayer context, and community notes.',
    accentOrbClass: 'bg-sky-400/30',
  },
  quran: {
    browserTitle: 'Quran — Islam | PakPoints',
    metaDescription: 'Quran reading and listening experiences on PakPoints—crafted for clarity and calm focus.',
    icon: '📖',
    heading: 'Holy Quran',
    tagline: 'Immersive reading modes, recitation highlights, and bookmarks—built for daily reflection.',
    accentOrbClass: 'bg-emerald-400/30',
  },
  hadith: {
    browserTitle: 'Hadith — Islam | PakPoints',
    metaDescription: 'Explore curated Hadith collections and themes on PakPoints.',
    icon: '💬',
    heading: 'Hadith',
    tagline: 'Searchable collections with themes, narrators, and authenticity notes—designed for study.',
    accentOrbClass: 'bg-violet-400/30',
  },
  calendar: {
    browserTitle: 'Islamic Calendar — Islam | PakPoints',
    metaDescription: 'Hijri calendar, key Islamic dates, and reminders on PakPoints.',
    icon: '📅',
    heading: 'Islamic Calendar',
    tagline: 'Track Ramadan, Eid, Ashura, and Dhul Hijjah with a luminous timeline and gentle reminders.',
    accentOrbClass: 'bg-amber-400/25',
  },
  zakat: {
    browserTitle: 'Zakat Calculator — Islam | PakPoints',
    metaDescription: 'Estimate Zakat on PakPoints—simple calculator with educational guidance.',
    icon: '💰',
    heading: 'Zakat Calculator',
    tagline: 'A quick, transparent estimate—pair it with local guidance and official nisab thresholds.',
    accentOrbClass: 'bg-rose-400/25',
  },
  education: {
    browserTitle: 'Islamic Education — Islam | PakPoints',
    metaDescription: 'Islamic courses and learning journeys on PakPoints.',
    icon: '🎓',
    heading: 'Education',
    tagline: 'Structured tracks—from essentials to advanced—with progress signals and community study rooms.',
    accentOrbClass: 'bg-indigo-400/30',
  },
};

function formatCurrency(n) {
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(
      n,
    );
  } catch {
    return n.toFixed(2);
  }
}

/**
 * Simple illustrative Zakat estimate (2.5% of entered zakatable amount). Not financial advice.
 */
function ZakatEstimatePanel() {
  const [cash, setCash] = useState('');
  const [savings, setSavings] = useState('');
  const [other, setOther] = useState('');

  const { total, zakat } = useMemo(() => {
    const a = parseFloat(cash) || 0;
    const b = parseFloat(savings) || 0;
    const c = parseFloat(other) || 0;
    const t = Math.max(0, a + b + c);
    return { total: t, zakat: t * 0.025 };
  }, [cash, savings, other]);

  return (
    <div className="space-y-8">
      <p className="text-sm leading-relaxed text-slate-400">
        Enter approximate zakatable assets (after debts you owe today). Official nisab varies by locality and school
        of thought—use this as a planning aid only and consult a qualified scholar or accountant.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { id: 'cash', label: 'Cash on hand', value: cash, onChange: setCash },
          { id: 'savings', label: 'Bank / savings', value: savings, onChange: setSavings },
          { id: 'other', label: 'Gold & other (value)', value: other, onChange: setOther },
        ].map((field) => (
          <label key={field.id} className="block text-left">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              {field.label}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-slate-900/60 px-4 py-3 text-slate-100 outline-none ring-cyan-400/40 transition placeholder:text-slate-600 focus:border-cyan-400/50 focus:ring-2"
              placeholder="0"
            />
          </label>
        ))}
      </div>
      <div className="grid gap-4 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 to-violet-600/10 p-6 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Zakatable total</p>
          <p className="mt-1 text-2xl font-bold text-white">{formatCurrency(total)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Illustrative Zakat (2.5%)</p>
          <p className="mt-1 text-2xl font-bold text-cyan-200">{formatCurrency(zakat)}</p>
        </div>
      </div>
    </div>
  );
}

function SectionBody({ slug }) {
  if (slug === 'mosques') {
    return (
      <div className="space-y-8 text-left">
        <p className="text-slate-300">
          Listings below are the same directory as the main site—open any card for details and directions.
        </p>
        <MosquesDirectoryList variant="islam" />
      </div>
    );
  }

  if (slug === 'zakat') {
    return <ZakatEstimatePanel />;
  }

  if (slug === 'education') {
    return (
      <div className="space-y-6 text-left">
        <p className="text-slate-300">
          Structured learning pieces published on PakPoints. For all articles and guides, see{' '}
          <Link to="/islam/articles" className="text-cyan-300 hover:text-cyan-200 underline">
            Islamic Articles
          </Link>
          .
        </p>
        <IslamContentList category="educational" language="en" detailBasePath="/islam/articles" />
      </div>
    );
  }

  if (slug === 'calendar') {
    const now = new Date();
    const gregorian = now.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const milestones = [
      { label: 'Ramadan', tone: 'from-emerald-400/30 to-teal-500/20' },
      { label: 'Eid al-Fitr', tone: 'from-sky-400/30 to-cyan-500/20' },
      { label: 'Day of Arafah', tone: 'from-amber-400/30 to-orange-500/20' },
      { label: 'Eid al-Adha', tone: 'from-violet-400/30 to-fuchsia-500/20' },
    ];
    return (
      <div className="space-y-8 text-left">
        <p className="rounded-xl border border-white/10 bg-slate-900/40 px-4 py-3 text-slate-300">
          <span className="font-semibold text-white">Today (Gregorian):</span> {gregorian}
        </p>
        <p className="text-slate-300">
          Hijri alignment, moon-sighting preferences, and smart reminders will layer here—designed to feel calm, not
          cluttered.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {milestones.map((m) => (
            <div
              key={m.label}
              className={`rounded-xl border border-white/10 bg-gradient-to-br ${m.tone} px-4 py-4 text-center text-sm font-semibold text-white backdrop-blur-sm`}
            >
              {m.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const copy = {
    quran: {
      lead: 'Crystal typography, night modes, and verse-level audio sync—crafted for long reading sessions.',
      bullets: ['Reciter playlists with seamless handoff', 'Ayah bookmarks synced across devices', 'Tafsir snippets inline'],
    },
    hadith: {
      lead: 'Collections grouped by theme—family, character, worship—with narrator chains summarized visually.',
      bullets: ['Topic journeys with guided reflections', 'Authenticity tags per collection', 'Share cards for study circles'],
    },
  };

  const block = copy[slug];
  if (!block) return null;

  return (
    <div className="space-y-6 text-left">
      <p className="text-lg text-slate-200">{block.lead}</p>
      <ul className="space-y-3 text-slate-300">
        {block.bullets.map((b) => (
          <li key={b} className="flex gap-3">
            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-gradient-to-r from-cyan-400 to-violet-400" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <p className="rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 text-sm text-slate-400">
        These experiences are rolling out on PakPoints—bookmark this hub and revisit as we ship each layer.
      </p>
    </div>
  );
}

/**
 * Purpose: Dynamic Islam subsection pages (mosques, Quran, Hadith, calendar, Zakat, education).
 * Inputs: URL param `sectionSlug`.
 * Outputs: Futuristic section page or redirect to hub when slug is unknown.
 */
const IslamSectionPage = () => {
  const { sectionSlug } = useParams();
  if (!VALID_SLUGS.includes(sectionSlug)) {
    return <Navigate to="/islam" replace />;
  }
  const cfg = SECTION_CONFIG[sectionSlug];

  return (
    <IslamFuturisticShell
      browserTitle={cfg.browserTitle}
      metaDescription={cfg.metaDescription}
      icon={cfg.icon}
      heading={cfg.heading}
      tagline={cfg.tagline}
      accentOrbClass={cfg.accentOrbClass}
      wide={sectionSlug === 'mosques'}
      noIndex={NOINDEX_SECTION_SLUGS.has(sectionSlug)}
    >
      <SectionBody slug={sectionSlug} />
    </IslamFuturisticShell>
  );
};

export default IslamSectionPage;
